import { useState } from 'react';
import { COUNTRIES } from 'constants.js';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { UNSUPPORTED_CREDIT_CARDS } from 'constants/paymentsSpa';
import paymentHelper from 'pages/PaymentsSpa/helper';
import useAmplitude from 'utils/hooks/useAmplitude';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import useTranslation from 'utils/hooks/useTranslation';

const useCardForm = ({ form, corporateClientId, onSubmit }) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const { executeRecaptcha } = useRecaptcha();
  const [creditCardType, setCreditCardType] = useState('');
  const [isCvcValid, setCvcValidation] = useState(false);
  const [isCreditCardValid, setCreditCardValidation] = useState(false);
  const [isZipCodeRequired, setIsZipCodeRequired] = useState(true);

  const handleCreditCardChange = ({ valid, type }) => {
    setCreditCardValidation(valid);
    setCreditCardType(type);
    form.setFieldsValue({ paymentCardType: type });
    form.validateFields(['cardNumber']).catch(() => {});
  };

  const handleCvcChange = ({ valid }) => setCvcValidation(valid);

  const handleExpiryChange = expiryDates => paymentHelper.validateExpiryDate(expiryDates);

  const handleCountryChange = countryCode => {
    setIsZipCodeRequired(
      countryCode === COUNTRIES.CA.countryCode || countryCode === COUNTRIES.US.countryCode,
    );
  };

  const handleSubmit = async () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.paymentSave);
    window.plausible?.('add & agree card clicked');

    try {
      const values = await form.validateFields();
      const recaptchaToken = await executeRecaptcha('submit');

      values.cardNumber = values.cardNumber.value.replace(/\s/g, '');
      values.cvv = values.cvv.value;
      values.recaptchaToken = recaptchaToken;
      values.corporateClientId = corporateClientId;
      if (values.billingAddress.zipCode && values.billingAddress.zipCode.replace(/\s/g, '')) {
        values.billingAddress.zipCode = values.billingAddress.zipCode.replace(/\s/g, '');
      } else {
        values.billingAddress = null;
      }
      onSubmit(values);
    } catch (error) {}
  };

  const expiryMonths = paymentHelper.buildExpiryMonths();

  const cardNumberRules = [
    {
      required: true,
      message: translateText('corporateAccounts.payments.form.cardNumberRequired'),
    },
    {
      validator: () =>
        UNSUPPORTED_CREDIT_CARDS.find(card => card.type === creditCardType)
          ? Promise.reject()
          : Promise.resolve(),
      message: translateText('corporateAccounts.payments.form.creditCardTypeNotSupported', {
        creditCardType,
      }),
    },
    {
      validator: (rule, value) => (isCreditCardValid ? Promise.resolve() : Promise.reject()),
      message: translateText('corporateAccounts.payments.form.cardNumberValidation'),
    },
  ];

  const cardTypeRules = [
    {
      required: true,
      message: translateText('corporateAccounts.payments.form.cardTypeRequired'),
    },
  ];

  const nameOnCardRules = [
    {
      required: true,
      message: translateText('corporateAccounts.payments.form.nameOnCardRequired'),
    },
    {
      pattern: new RegExp('^[^<>"\'&]+$'),
      message: translateText('corporateAccounts.payments.form.nameOnCardFormat'),
    },
    { max: 70, message: translateText('corporateAccounts.payments.form.nameOnCardLength') },
    { min: 2, message: translateText('corporateAccounts.payments.form.nameOnCardLength') },
  ];

  const expiryMonthRules = [
    {
      required: true,
      message: translateText(
        'corporateAccounts.corporateClient.payment.expiryMonth.error.presence',
      ),
    },
    {
      validator: (rule, value) =>
        handleExpiryChange({ month: value, year: form.getFieldValue('expiryYear') })
          ? Promise.resolve()
          : Promise.reject(),
      message: translateText('corporateAccounts.payments.form.cardExpiryValidation'),
    },
  ];

  const expiryYearRules = [
    { max: 4, message: translateText('corporateAccounts.payments.form.cardExpiryValidation') },
    {
      required: true,
      message: translateText('corporateAccounts.corporateClient.payment.expiryYear.error.presence'),
    },
    {
      validator: (rule, value) =>
        handleExpiryChange({ month: form.getFieldValue('expiryMonth'), year: value })
          ? Promise.resolve()
          : Promise.reject(),
      message: translateText('corporateAccounts.payments.form.cardExpiryValidation'),
    },
  ];

  const cvvRules = [
    { required: true, message: translateText('corporateAccounts.payments.form.cardCvcRequired') },
    {
      validator: (rule, value) => (isCvcValid ? Promise.resolve() : Promise.reject()),
      message: translateText('corporateAccounts.payments.form.cardCvcValidation'),
    },
  ];

  const zipCodeRules = [
    {
      required: isZipCodeRequired,
      message: translateText('corporateAccounts.payments.form.zipCodeRequired'),
    },
  ];

  const countryCodeRules = [
    {
      required: true,
      message: translateText('corporateAccounts.payments.form.cardCountryCodeRequired'),
    },
  ];

  return {
    creditCardType,
    isZipCodeRequired,
    expiryMonths,
    handleCreditCardChange,
    handleCvcChange,
    handleExpiryChange,
    handleCountryChange,
    handleSubmit,
    cardNumberRules,
    cardTypeRules,
    nameOnCardRules,
    expiryMonthRules,
    expiryYearRules,
    cvvRules,
    zipCodeRules,
    countryCodeRules,
  };
};

export default useCardForm;
